import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Text, Badge, Input, Spinner, Button } from '@fluentui/react-components';
import {
  AddRegular,
  CalendarRegular,
  WarningRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import {
  PermissionGate,
  ProtectedButton,
  SearchableDropdown,
  TablePagination,
} from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDate,
} from '../../../configurations/index.config';
import type { IncidentReport, Site, Employee, PaginationMeta } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '1px solid #edebe9',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f3f2f1',
      cursor: 'pointer',
    },
  },
  td: {
    padding: '12px 16px',
    color: '#323130',
  },
  incidentCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#c8c6c4',
    marginBottom: '16px',
  },
});

const IncidentReportsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState<IncidentReport[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchSites();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [
    currentPage,
    itemsPerPage,
    selectedSiteId,
    selectedSeverity,
    selectedStatus,
    startDate,
    endDate,
  ]);

  const fetchSites = async () => {
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        setEmployees(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (selectedSiteId) {
        params.siteId = selectedSiteId;
      }
      if (selectedSeverity !== 'all') {
        params.severity = selectedSeverity;
      }
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await ApiClient.get('/get-incident-reports', { params });

      if (response.data.status === 'success') {
        setIncidents(response.data.data || []);
        setPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleViewDetails = (id: number) => {
    navigate(`/account/incident-details/${id}`);
  };

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, any> = {
      low: { color: 'informative', label: 'Low' },
      medium: { color: 'warning', label: 'Medium' },
      high: { color: 'important', label: 'High' },
      critical: { color: 'danger', label: 'Critical' },
    };

    const config = colors[severity] || { color: 'subtle', label: severity };

    return (
      <Badge appearance="filled" color={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, any> = {
      open: { color: 'danger', label: 'Open' },
      in_progress: { color: 'warning', label: 'In Progress' },
      resolved: { color: 'success', label: 'Resolved' },
      closed: { color: 'subtle', label: 'Closed' },
    };

    const config = colors[status] || { color: 'subtle', label: status };

    return (
      <Badge appearance="tint" color={config.color}>
        {config.label}
      </Badge>
    );
  };

  return (
    <PermissionGate privilege="incidents.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Incident Reports</Text>
          <Text className={styles.pageSubtitle}>
            Track and manage security incidents reported by guards
          </Text>
        </div>

        <ContentCard>
          <TableCommandBar
            title={`Incidents (${pagination?.totalItems || 0})`}
            actions={
              <>
                <Button appearance="secondary" icon={<ArrowDownloadRegular />}>
                  Export
                </Button>
                <ProtectedButton
                  privilege="incidents.create"
                  appearance="primary"
                  icon={<AddRegular />}
                  onClick={() => navigate('/account/create-incident')}
                >
                  Create Incident
                </ProtectedButton>
              </>
            }
          />

          <div className={styles.filters}>
            <Input
              type="date"
              contentBefore={<CalendarRegular />}
              placeholder="Start Date"
              value={startDate}
              onChange={e => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />

            <Input
              type="date"
              contentBefore={<CalendarRegular />}
              placeholder="End Date"
              value={endDate}
              onChange={e => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />

            <SearchableDropdown
              placeholder="Filter by Site"
              selectedValue={selectedSiteId ? selectedSiteId.toString() : 'all'}
              onSelect={value => {
                setSelectedSiteId(value === 'all' ? undefined : Number(value));
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Sites' },
                ...sites.map(site => ({
                  id: site.id.toString(),
                  label: site.name,
                })),
              ]}
            />

            <SearchableDropdown
              placeholder="Filter by Severity"
              selectedValue={selectedSeverity}
              onSelect={value => {
                setSelectedSeverity(value);
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Severity' },
                { id: 'low', label: 'Low' },
                { id: 'medium', label: 'Medium' },
                { id: 'high', label: 'High' },
                { id: 'critical', label: 'Critical' },
              ]}
            />

            <SearchableDropdown
              placeholder="Filter by Status"
              selectedValue={selectedStatus}
              onSelect={value => {
                setSelectedStatus(value);
                setCurrentPage(1);
              }}
              options={[
                { id: 'all', label: 'All Status' },
                { id: 'open', label: 'Open' },
                { id: 'in_progress', label: 'In Progress' },
                { id: 'resolved', label: 'Resolved' },
                { id: 'closed', label: 'Closed' },
              ]}
            />
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <Spinner label="Loading incidents..." />
            </div>
          ) : incidents.length === 0 ? (
            <div className={styles.emptyState}>
              <WarningRegular className={styles.emptyStateIcon} />
              <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                No incidents found
              </Text>
              <Text style={{ color: '#605e5c' }}>Try adjusting your filters or date range</Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Incident</th>
                  <th className={styles.th}>Site</th>
                  <th className={styles.th}>Reported By</th>
                  <th className={styles.th}>Date</th>
                  <th className={styles.th}>Severity</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map(incident => (
                  <tr
                    key={incident.id}
                    className={styles.tr}
                    onClick={() => handleViewDetails(incident.id)}
                  >
                    <td className={styles.td}>
                      <div className={styles.incidentCell}>
                        <Text weight="semibold">{incident.incident_type}</Text>
                        <Text size={200} style={{ color: '#605e5c' }}>
                          {incident.description.substring(0, 60)}...
                        </Text>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <Text>{incident.site_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{incident.employee_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text size={300}>{fullDate(incident.incident_timestamp)}</Text>
                    </td>
                    <td className={styles.td}>{getSeverityBadge(incident.severity)}</td>
                    <td className={styles.td}>{getStatusBadge(incident.status)}</td>
                    <td className={styles.td}>
                      <Text>{incident.assigned_to_name || 'Unassigned'}</Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && pagination && incidents.length > 0 && (
            <TablePagination
              totalItems={pagination.totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={value => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default IncidentReportsFluentUI;
